import { alpha, Box, Button, darken, lighten, styled, useMediaQuery } from "@mui/material";
import { fontWeight } from "config/Theme/options";
import { useDefaultLayoutContext } from "context/DefaultLayoutContext";
import { useMUIThemeModeContext } from "context/MUIThemeModeContext";
import { SubscriptionType } from "types/enums";
import { getColorByMode } from "utils";

export const Card = styled(Box)<{ subscription: SubscriptionType }>(({ theme, subscription }) => {
  const { colors } = useMUIThemeModeContext();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return {
    display: "flex",
    position: "relative",
    borderRadius: "10px",
    padding: isMobile ? theme.spacing(3, 2) : theme.spacing(4, 3),
    height: "100%",
    gap: theme.spacing(2.25),
    flexDirection: "column",
    alignItems: "flex-start",
    ...(subscription === SubscriptionType.FREE && {
      backgroundColor: colors.mintWhite,
      color: colors.gray600,
    }),
    ...(subscription === SubscriptionType.PREMIUM && {
      background: `linear-gradient(60deg, ${colors.royalPurple} 0%, ${alpha(colors.royalPurple, 0.8)} 100%)`,
      color: colors.gray100,
    }),
    ...(subscription === SubscriptionType.PRO && {
      background: `linear-gradient(60deg, 
        ${getColorByMode(theme, colors.deepMidnightBlue, colors.gray800)} 0%,
        ${alpha(getColorByMode(theme, colors.deepMidnightBlue, colors.gray800), 0.9)} 100%)`,
      color: colors.gray100,
    }),
  };
});

export const Badge = styled(Box)(({ theme }) => {
  const { isDrawerOpen } = useDefaultLayoutContext();

  return {
    position: "absolute",
    top: 0,
    right: theme.spacing(3),
    padding: theme.spacing(1.5, 2),
    borderBottomLeftRadius: "10px",
    borderBottomRightRadius: "10px",
    "& .MuiTypography-root": {
      fontSize: "1rem",
      fontWeight: fontWeight.semiBold,
    },
    [theme.breakpoints.down(1360)]: {
      ...(isDrawerOpen && {
        right: theme.spacing(2),
        "& .MuiTypography-root": {
          fontSize: "0.875rem",
        },
      }),
    },
    [theme.breakpoints.down(1260)]: {
      ...(isDrawerOpen && {
        right: theme.spacing(1.5),
        padding: theme.spacing(1, 1.5),
        "& .MuiTypography-root": {
          fontSize: "0.875rem",
        },
      }),
    },
  };
});

export const SubscriptionButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "subscription",
})<{ subscription: SubscriptionType }>(({ theme, subscription }) => {
  const { colors } = useMUIThemeModeContext();
  const hoverColor = getColorByMode(theme, darken(colors.white, 0.1), lighten(colors.white, 0.15));
  
  const color = subscription === SubscriptionType.FREE ? colors.black : colors.white;
  const darkColor = subscription === SubscriptionType.PRO ? colors.white : colors.gray800;
  
  const borderColor = "#C2D5CD";
  const borderDarkColor = subscription === SubscriptionType.PRO ? colors.gray600 : "#C2D5CD";

  return {
    borderRadius: "12px",
    fontSize: "0.9375rem",
    backgroundColor: colors.white,
    color: colors.black,

    "&:hover": {
      backgroundColor: hoverColor,
    },

    "&:disabled": {
      backgroundColor: "transparent",
      color: getColorByMode(theme, color, darkColor),
      border: `1px solid ${getColorByMode(theme, borderColor, borderDarkColor)}`,
    },
  };
});
